import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  ProvSetStudent,
  ProvQuestion,
  StudentGradeResult,
  startAttempt,
  saveAnswer,
  completeAttempt,
  studentGradeAttempt,
  revealAnswer,
  getErrorMessage,
} from '../provApi';

const BRAND_COLOR = '#1a365d';

interface StudentQuizProps {
  set: ProvSetStudent;
  onBack: () => void;
  onComplete: () => void;
}

export default function StudentQuiz({ set, onBack, onComplete }: StudentQuizProps) {
  const isGlosor = set.type === 'glosor';

  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [revealedAnswers, setRevealedAnswers] = useState<Record<string, string>>({});
  const [lockedQuestions, setLockedQuestions] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  // AI grading state
  const [gradeResult, setGradeResult] = useState<StudentGradeResult | null>(null);
  const [grading, setGrading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSavesRef = useRef(0);
  const failedSaveRef = useRef<{ questionId: string; text: string } | null>(null);
  const questions: ProvQuestion[] = set.questions;
  const currentQ = questions[currentIndex];
  const totalQuestions = questions.length;
  const answeredCount = Object.values(answers).filter((a) => a.trim().length > 0).length;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const canReveal = set.revealAfterAnswer;
  const isCurrentLocked = lockedQuestions[currentQ?._id] || false;
  const isCurrentRevealed = !!revealedAnswers[currentQ?._id];
  const hasCurrentAnswer = (answers[currentQ?._id] || '').trim().length > 0;

  // Check if current question is a böjning question
  const currentQForms = currentQ?.forms && currentQ.forms.length > 0 ? currentQ.forms : null;

  // Start or resume attempt (only in write mode)
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const result = await startAttempt(set._id);
        if (!cancelled) {
          setAttemptId(result.attemptId);
          if (result.resumed && result.existingAnswers) {
            setAnswers(result.existingAnswers);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(getErrorMessage(err));
        }
      }
    };
    init();
    return () => { cancelled = true; };
  }, [set._id]);

  const saveImmediate = useCallback(
    async (questionId: string, text: string) => {
      if (!attemptId) return;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      pendingSavesRef.current += 1;
      setSaving(true);
      try {
        await saveAnswer(attemptId, questionId, text);
        failedSaveRef.current = null;
        setSaveError(null);
      } catch (err) {
        failedSaveRef.current = { questionId, text };
        setSaveError(getErrorMessage(err));
        throw err;
      } finally {
        pendingSavesRef.current -= 1;
        setSaving(false);
      }
    },
    [attemptId]
  );

  const debouncedSave = useCallback(
    (questionId: string, text: string) => {
      if (!attemptId) return;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveImmediate(questionId, text).catch(() => undefined);
      }, 800);
    },
    [attemptId, saveImmediate]
  );

  const ensureAnswersSaved = useCallback(async () => {
    if (!attemptId || !currentQ) {
      throw new Error('Provet är inte redo att lämnas in ännu.');
    }
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (!isCurrentLocked) {
      await saveImmediate(currentQ._id, answers[currentQ._id] || '');
    } else if (failedSaveRef.current) {
      await saveImmediate(failedSaveRef.current.questionId, failedSaveRef.current.text);
    }
    if (failedSaveRef.current) {
      throw new Error('Svaret kunde inte sparas. Försök igen innan du lämnar in.');
    }
  }, [answers, attemptId, currentQ, isCurrentLocked, saveImmediate]);

  const handleAnswerChange = (text: string) => {
    if (isCurrentLocked) return;
    const qId = currentQ._id;
    setAnswers((prev) => ({ ...prev, [qId]: text }));
    debouncedSave(qId, text);
  };

  // For böjning: update a single form answer within the combined string
  // Stores as "label: value\nlabel: value" to match the correct answer format for AI grading
  const handleFormAnswerChange = (formIndex: number, value: string) => {
    if (isCurrentLocked || !currentQForms) return;
    const qId = currentQ._id;
    const currentCombined = answers[qId] || '';
    const parts = currentQForms.map((f, i) => {
      // Extract current value for this form (strip "label: " prefix if present)
      const prevLine = currentCombined.split('\n')[i] || '';
      const prevValue = prevLine.includes(': ') ? prevLine.split(': ').slice(1).join(': ') : prevLine;
      const newValue = i === formIndex ? value : prevValue;
      return `${f.label}: ${newValue}`;
    });
    const combined = parts.join('\n');
    setAnswers((prev) => ({ ...prev, [qId]: combined }));
    debouncedSave(qId, combined);
  };

  const getFormAnswer = (formIndex: number): string => {
    if (!currentQForms) return '';
    const combined = answers[currentQ._id] || '';
    const line = combined.split('\n')[formIndex] || '';
    // Strip the "label: " prefix to get just the value for the input field
    return line.includes(': ') ? line.split(': ').slice(1).join(': ') : line;
  };

  const handleRevealAnswer = async () => {
    if (!attemptId || !currentQ) return;
    const qId = currentQ._id;
    const currentAnswer = answers[qId] || '';

    if (currentAnswer.trim().length > 0) {
      await saveImmediate(qId, currentAnswer);
    }

    setLockedQuestions((prev) => ({ ...prev, [qId]: true }));

    try {
      const result = await revealAnswer(attemptId, qId);
      setRevealedAnswers((prev) => ({ ...prev, [qId]: result.correctAnswer }));
    } catch (err) {
      setError(getErrorMessage(err));
      setLockedQuestions((prev) => ({ ...prev, [qId]: false }));
    }
  };

  const navigateTo = async (newIndex: number) => {
    const currentAnswer = answers[currentQ._id] || '';
    if (currentAnswer.trim().length > 0 && !isCurrentLocked) {
      await saveImmediate(currentQ._id, currentAnswer);
    }
    setCurrentIndex(newIndex);
  };

  const handleComplete = async () => {
    if (!attemptId) return;
    setCompleting(true);
    setError(null);

    try {
      await ensureAnswersSaved();
      await completeAttempt(attemptId);
      setCompleted(true);
    } catch (err) {
      setError(err instanceof Error && err.message.includes('sparas')
        ? err.message
        : getErrorMessage(err));
    } finally {
      setCompleting(false);
      setShowConfirmComplete(false);
    }
  };

  const handleGrade = async () => {
    if (!attemptId) return;
    setGrading(true);
    setError(null);

    try {
      if (!completed) {
        await ensureAnswersSaved();
      }
      const result = await studentGradeAttempt(attemptId);
      setGradeResult(result);
      setShowResults(true);
    } catch (err) {
      setError(err instanceof Error && err.message.includes('sparas')
        ? err.message
        : getErrorMessage(err));
    } finally {
      setGrading(false);
    }
  };

  // =========================================================================
  // Results screen
  // =========================================================================

  if (showResults && gradeResult) {
    const { summary, perQuestion } = gradeResult;
    const pct = summary.percentage ?? 0;
    const stars = pct >= 90 ? 3 : pct >= 60 ? 2 : pct >= 30 ? 1 : 0;

    const messages = [
      'Fortsätt öva, du lär dig mer varje gång!',
      'Bra start! Du är på rätt väg!',
      'Riktigt bra jobbat!',
      'Fantastiskt! Du kan det här!',
    ];

    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', pb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <EmojiEventsIcon sx={{ fontSize: { xs: 50, sm: 64 }, color: '#FFD700', mb: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1.5 }}>
            {[1, 2, 3].map((n) => (
              <StarIcon
                key={n}
                sx={{
                  fontSize: { xs: 32, sm: 40 },
                  color: n <= stars ? '#FFD700' : '#e0e0e0',
                  transition: 'all 0.3s',
                  transform: n <= stars ? 'scale(1)' : 'scale(0.8)',
                }}
              />
            ))}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, color: BRAND_COLOR }}>
            {summary.totalScore ?? 0} av {summary.maxScore ?? 0} poäng
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
            {pct}%
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {isGlosor
              ? `${summary.correctCount ?? 0} helt rätt${(summary.partialCount ?? 0) > 0 ? `, ${summary.partialCount} delvis rätt` : ''}, ${summary.incorrectCount ?? 0} fel`
              : `${summary.correctCount ?? 0} rätt, ${summary.partialCount ?? 0} delvis rätt, ${summary.incorrectCount ?? 0} fel`}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' }, color: 'text.primary', mt: 1 }}>
            {messages[stars]}
          </Typography>
          {summary.aiFeedback && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', maxWidth: 500, mx: 'auto' }}>
              {summary.aiFeedback}
            </Typography>
          )}
        </Box>

        <Typography sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Facit från provet
        </Typography>
        {questions.map((q, idx) => {
          const qResult = perQuestion.find((p) => p.questionId === q._id);
          const aiScore = qResult?.aiScore ?? 0;
          const aiFeedback = qResult?.aiFeedback;
          const textbookAnswer = (qResult?.correctAnswer || '').trim();
          const acceptableAnswers = (qResult?.acceptableAnswers || [])
            .map((value) => value.trim())
            .filter((value) => value && value !== textbookAnswer);
          const studentAnswer = answers[q._id] || '';
          const qFormCount = q.forms && q.forms.length > 0 ? q.forms.length : 0;
          const qMaxScore = isGlosor ? (qFormCount > 1 ? qFormCount : 1) : 2;
          const isFullScore = aiScore >= qMaxScore;
          const isPartialScore = !isFullScore && aiScore > 0;
          const scoreColor = isFullScore ? '#4CAF50' : isPartialScore ? '#FF9800' : '#f44336';
          const scoreBg = isFullScore ? '#E8F5E9' : isPartialScore ? '#FFF8E1' : '#FFEBEE';
          const scoreBorder = isFullScore ? '#A5D6A7' : isPartialScore ? '#FFE082' : '#EF9A9A';
          const scoreLabel = isGlosor
            ? (qFormCount > 1 ? `${aiScore} av ${qMaxScore} rätt` : (aiScore >= 1 ? 'Rätt (1p)' : 'Fel (0p)'))
            : (aiScore === 2 ? 'Rätt (2p)' : aiScore === 1 ? 'Delvis rätt (1p)' : 'Fel (0p)');
          const ScoreIcon = isFullScore ? CheckCircleIcon : isPartialScore ? RemoveCircleOutlineIcon : CancelIcon;
          const facitLines = textbookAnswer.split('\n').map((line) => line.trim()).filter(Boolean);

          return (
            <Card
              key={q._id}
              sx={{ mb: 1.5, borderRadius: 2, border: '1px solid', borderColor: scoreBorder, bgcolor: scoreBg }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <ScoreIcon sx={{ color: scoreColor, fontSize: 24, mt: 0.3, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        {idx + 1}. {q.prompt}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.25,
                        borderRadius: 2,
                        bgcolor: '#E8F5E9',
                        border: '2px solid #1B5E20',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: '#1B5E20',
                          fontSize: { xs: '0.8rem', sm: '0.85rem' },
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          mb: 0.5,
                        }}
                      >
                        Rätt svar
                      </Typography>
                      {textbookAnswer ? (
                        facitLines.length > 1 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {facitLines.map((line, lineIdx) => (
                              <Typography
                                key={`${q._id}-facit-${lineIdx}`}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: '1.05rem', sm: '1.2rem' },
                                  color: '#1B5E20',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '1.05rem', sm: '1.2rem' },
                              color: '#1B5E20',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {textbookAnswer}
                          </Typography>
                        )
                      ) : (
                        <Typography sx={{ fontWeight: 700, color: '#1B5E20' }}>
                          Facit saknas i provet.
                        </Typography>
                      )}
                      {acceptableAnswers.length > 0 && (
                        <Typography variant="body2" sx={{ mt: 1, color: '#1B5E20', fontWeight: 600 }}>
                          Också godkänt: {acceptableAnswers.join(', ')}
                        </Typography>
                      )}
                    </Box>
                    {studentAnswer.trim() ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Ditt svar: {studentAnswer.replace(/\n/g, ', ')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.disabled', mb: 1, fontStyle: 'italic' }}>
                        (Inget svar)
                      </Typography>
                    )}
                    <Chip
                      label={scoreLabel}
                      size="small"
                      sx={{ bgcolor: 'white', fontWeight: 600, color: scoreColor, fontSize: '0.75rem' }}
                    />
                    {aiFeedback && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        Kommentar: {aiFeedback}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={onComplete}
            sx={{
              minHeight: { xs: 48, sm: 56 },
              px: { xs: 4, sm: 6 },
              borderRadius: 2,
              bgcolor: BRAND_COLOR,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              '&:hover': { bgcolor: '#0f2744' },
            }}
          >
            Tillbaka till listan
          </Button>
        </Box>
      </Box>
    );
  }

  // =========================================================================
  // Completion screen (before grading)
  // =========================================================================

  if (completed && !showResults) {
    return (
      <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 6 } }}>
        <EmojiEventsIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#FFD700', mb: 1 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Bra jobbat!
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Du svarade på {answeredCount} av {totalQuestions} {isGlosor ? 'ord' : 'frågor'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {set.title}
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: '0.9rem' }}>
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={grading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
          onClick={handleGrade}
          disabled={grading}
          sx={{
            minHeight: { xs: 52, sm: 60 },
            px: { xs: 4, sm: 6 },
            borderRadius: 3,
            bgcolor: '#7C4DFF',
            fontSize: { xs: '1.05rem', sm: '1.2rem' },
            fontWeight: 700,
            textTransform: 'none',
            mb: 2,
            '&:hover': { bgcolor: '#651FFF' },
          }}
        >
          {grading ? 'AI rättar...' : 'Rätta mitt prov!'}
        </Button>

        <Box>
          <Button
            variant="text"
            onClick={onComplete}
            sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' }, textTransform: 'none' }}
          >
            Hoppa över, tillbaka till listan
          </Button>
        </Box>
      </Box>
    );
  }

  // =========================================================================
  // Loading state
  // =========================================================================

  if (!attemptId) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary">
          {error || 'Startar provet...'}
        </Typography>
        {error && (
          <Button onClick={onBack} sx={{ mt: 2 }}>
            Tillbaka
          </Button>
        )}
      </Box>
    );
  }

  // =========================================================================
  // Write mode quiz in progress
  // =========================================================================

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', pb: 4 }}>
      {/* Progress bar */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {isGlosor ? 'Ord' : 'Fråga'} {currentIndex + 1} av {totalQuestions}
          </Typography>
          <Chip
            label={`${answeredCount}/${totalQuestions} besvarade`}
            size="small"
            color={answeredCount === totalQuestions ? 'success' : 'default'}
            sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: { xs: 6, sm: 8 },
            borderRadius: 4,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              bgcolor: answeredCount === totalQuestions ? '#4CAF50' : BRAND_COLOR,
            },
          }}
        />
      </Box>

      {/* Question card */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          mb: { xs: 2, sm: 3 },
          border: '2px solid',
          borderColor: isCurrentRevealed ? '#4CAF50' : '#e3e8ef',
          transition: 'border-color 0.3s',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography
            variant="overline"
            sx={{ color: BRAND_COLOR, fontWeight: 600, letterSpacing: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
          >
            {isGlosor ? `Ord ${currentIndex + 1}` : `Fråga ${currentIndex + 1}`}
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              mt: 1,
              mb: { xs: 2, sm: 3 },
              lineHeight: 1.5,
              fontSize: { xs: '1.1rem', sm: '1.4rem' },
            }}
          >
            {currentQ.prompt}
          </Typography>

          {/* Question image */}
          {currentQ.imageUrl && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Box
                component="img"
                src={currentQ.imageUrl}
                alt={`Bild till ${isGlosor ? 'ord' : 'fråga'} ${currentIndex + 1}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: { xs: 200, sm: 300 },
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}

          {/* Answer input: böjning (multiple fields) or standard (single field) */}
          {currentQForms ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
                Böj ordet — fyll i alla former:
              </Typography>
              {currentQForms.map((form, fIdx) => (
                <TextField
                  key={`${currentQ._id}-${form.label}`}
                  fullWidth
                  label={form.label}
                  value={getFormAnswer(fIdx)}
                  onChange={(e) => handleFormAnswerChange(fIdx, e.target.value)}
                  variant="outlined"
                  disabled={isCurrentLocked}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: { xs: '1rem', sm: '1.05rem' },
                      '&.Mui-focused fieldset': { borderColor: BRAND_COLOR },
                    },
                  }}
                />
              ))}
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={isGlosor ? 1 : 2}
              maxRows={6}
              value={answers[currentQ._id] || ''}
              onChange={(e) => handleAnswerChange(e.target.value)}
              placeholder={isGlosor ? 'Skriv svaret...' : 'Skriv ditt svar här...'}
              variant="outlined"
              disabled={isCurrentLocked}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.6,
                  '&.Mui-focused fieldset': { borderColor: BRAND_COLOR },
                },
              }}
            />
          )}

          {saving && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Sparar...
            </Typography>
          )}
          {saveError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {saveError} Tryck Lämna in igen för att spara om.
            </Typography>
          )}

          {/* Reveal answer button */}
          {canReveal && hasCurrentAnswer && !isCurrentRevealed && (
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={handleRevealAnswer}
              sx={{
                mt: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                minHeight: { xs: 44, sm: 48 },
                borderColor: '#FFB300',
                color: '#E65100',
                '&:hover': { borderColor: '#FF8F00', bgcolor: '#FFF8E1' },
              }}
            >
              Visa rätt svar
            </Button>
          )}

          {/* Revealed answer */}
          <Collapse in={isCurrentRevealed}>
            <Box
              sx={{
                mt: 2,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                bgcolor: '#E8F5E9',
                border: '2px solid #A5D6A7',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32', display: 'block', mb: 0.75 }}>
                Rätt svar:
              </Typography>
              {currentQForms ? (
                // Böjning: parse "label: value\nlabel: value" and display as rows
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {(revealedAnswers[currentQ._id] || '')
                    .split('\n')
                    .filter(Boolean)
                    .map((pair, i) => {
                      const colonIdx = pair.indexOf(': ');
                      const label = colonIdx >= 0 ? pair.slice(0, colonIdx) : pair;
                      const value = colonIdx >= 0 ? pair.slice(colonIdx + 2) : '';
                      return (
                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                          <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 600, minWidth: 100 }}>
                            {label}:
                          </Typography>
                          <Typography sx={{ fontSize: { xs: '1rem', sm: '1.05rem' }, fontWeight: 700, color: '#1B5E20' }}>
                            {value}
                          </Typography>
                        </Box>
                      );
                    })}
                </Box>
              ) : (
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 500, color: '#1B5E20' }}>
                  {revealedAnswers[currentQ._id]}
                </Typography>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {error && (
        <Typography color="error" sx={{ mb: 2, textAlign: 'center', fontSize: '0.9rem' }}>
          {error}
        </Typography>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => navigateTo(currentIndex - 1)}
          disabled={currentIndex === 0}
          sx={{
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'divider',
            width: { xs: 48, sm: 56 },
            height: { xs: 48, sm: 56 },
            '&:hover': { bgcolor: '#f5f5f5' },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Question dots */}
        <Box sx={{ display: 'flex', gap: { xs: 0.4, sm: 0.5 }, flexWrap: 'wrap', justifyContent: 'center', flex: 1, px: 1 }}>
          {questions.map((q, idx) => {
            const isAnswered = (answers[q._id] || '').trim().length > 0;
            const isRevealed = !!revealedAnswers[q._id];
            const isCurrent = idx === currentIndex;
            return (
              <Box
                key={q._id}
                onClick={() => navigateTo(idx)}
                sx={{
                  width: { xs: 14, sm: 12 },
                  height: { xs: 14, sm: 12 },
                  borderRadius: '50%',
                  cursor: 'pointer',
                  bgcolor: isCurrent
                    ? BRAND_COLOR
                    : isRevealed
                      ? '#FFD700'
                      : isAnswered
                        ? '#4CAF50'
                        : '#e0e0e0',
                  border: isCurrent ? '2px solid' : 'none',
                  borderColor: BRAND_COLOR,
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.3)' },
                }}
              />
            );
          })}
        </Box>

        {currentIndex < totalQuestions - 1 ? (
          <IconButton
            onClick={() => navigateTo(currentIndex + 1)}
            sx={{
              bgcolor: BRAND_COLOR,
              color: 'white',
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              '&:hover': { bgcolor: '#0f2744' },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            onClick={() => setShowConfirmComplete(true)}
            startIcon={<SendIcon />}
            sx={{
              minHeight: { xs: 48, sm: 56 },
              borderRadius: 2,
              bgcolor: '#4CAF50',
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              '&:hover': { bgcolor: '#388E3C' },
            }}
          >
            Klar!
          </Button>
        )}
      </Box>

      {/* Confirm complete dialog */}
      <Dialog open={showConfirmComplete} onClose={() => setShowConfirmComplete(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Lämna in?</DialogTitle>
        <DialogContent>
          <Typography>
            Du har svarat på {answeredCount} av {totalQuestions} {isGlosor ? 'ord' : 'frågor'}.
            {answeredCount < totalQuestions && (
              <> Du har {totalQuestions - answeredCount} utan svar.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowConfirmComplete(false)} variant="outlined">
            Fortsätt svara
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            disabled={completing}
            sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}
          >
            {completing ? 'Lämnar in...' : 'Lämna in'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
